// Meeting notes at /o/:slug/meetings/:grackleId/notes (reset PR3 scaffold, DS artboard 1f /
// ui_kits/desktop/NotesScreen.jsx). FR-M6's summary, decisions and action items come from F1
// (notes generation), which needs a language model; until an org connects one this screen says
// so plainly and points admins at Settings → AI. What already exists is shown: the people's
// own marked moments, and the transcript in the 340px source panel. FR-M7: a generated line is
// only ever drawn with the Timestamp chip that ties it to its transcript span.
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ActionItem,
  Badge,
  Card,
  Decision,
  Icon,
  IconButton,
  Moment,
  Tabs,
  Timestamp,
  Utterance,
  VideoFrame,
} from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { GrackleDetail } from "../api/types";
import { AppShell } from "../components/AppShell";
import { PageLoader } from "../components/ui";
import { formatDuration } from "../library/format";
import RenameGrackleDialog from "../library/RenameGrackleDialog";
import { adminAiPath, meetingsPath } from "../library/routes";
import { getGrackleTranscript } from "../live/client";
import type { GrackleTranscript } from "../live/types";
import NotFound from "../pages/NotFound";
import { useSession } from "../session";
import { activeCueIndex, clock, cuesFromTranscript } from "../watch/transcript";
import { loadMeetingNotes, type MeetingNotes } from "./types";
import "./notes.css";

export const NOTES_EMPTY = "Notes need a language model. An org admin can connect one in Settings → AI.";

function meetingWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(d);
  const time = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(d);
  return `${day} · ${time}`;
}

/** Speakers keep a fixed colour (1–5) in order of first appearance. */
function speakerNumbers(names: string[]): Map<string, 1 | 2 | 3 | 4 | 5> {
  const map = new Map<string, 1 | 2 | 3 | 4 | 5>();
  for (const n of names) {
    if (!map.has(n)) map.set(n, ((map.size % 5) + 1) as 1 | 2 | 3 | 4 | 5);
  }
  return map;
}

export default function NotesPage() {
  const { slug, grackleId } = useParams();
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = orgs.find((o) => o.slug === slug);

  const [detail, setDetail] = useState<GrackleDetail | null>(null);
  const [transcript, setTranscript] = useState<GrackleTranscript | null>(null);
  const [notes, setNotes] = useState<MeetingNotes | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState("notes");
  const [source, setSource] = useState<number | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);

  useEffect(() => {
    if (!grackleId) return;
    let cancelled = false;
    setDetail(null);
    setNotFound(false);
    setLoadError(null);
    api<GrackleDetail>("GET", `/grackles/${grackleId}`)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (cancelled) return;
        // §4.10: missing and forbidden look the same.
        if (err instanceof ApiFail && err.status === 404) setNotFound(true);
        else setLoadError(err instanceof ApiFail ? err.message : "Couldn't load this meeting.");
      });
    getGrackleTranscript(grackleId)
      .then((t) => {
        if (!cancelled) setTranscript(t);
      })
      .catch(() => {
        // No transcript is an empty source panel, not a broken page (FR-AI7).
      });
    void loadMeetingNotes(grackleId).then((n) => {
      if (!cancelled) setNotes(n);
    });
    return () => {
      cancelled = true;
    };
  }, [grackleId]);

  const cues = useMemo(() => cuesFromTranscript(transcript), [transcript]);
  const speakers = useMemo(() => speakerNumbers(cues.map((c) => c.speaker ?? "Speaker")), [cues]);
  const moments = transcript?.bookmarks ?? [];
  const sourceIndex = source === null ? -1 : activeCueIndex(cues, source);

  if (sessionLoading) return <PageLoader label="Loading meeting…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org || !grackleId) return <Navigate to="/orgs" replace />;
  if (notFound) return <NotFound />;

  const isAdmin = org.role === "owner" || org.role === "admin";
  const ts = (t: number) => <Timestamp time={clock(t)} active={source === t} onClick={() => setSource(t)} />;

  return (
    <AppShell org={org} user={user} section="meetings" fullWidth>
      <div className="notes">
        <div className="notes__main">
          {loadError ? (
            <div className="notes__empty" role="alert">
              <p>{loadError}</p>
            </div>
          ) : !detail ? (
            <PageLoader label="Loading meeting…" />
          ) : (
            <>
              <div className="notes__head">
                <div className="gk-sm notes__crumb">
                  <Link to={meetingsPath(org.slug)}>Meetings</Link> / {detail.title}
                </div>
                <div className="notes__title-row">
                  <h1 className="gk-h2 notes__title">{detail.title}</h1>
                  {/* FR-L2: the owner renames here as from the library card (same rule). */}
                  {detail.owner.id === user.id ? (
                    <IconButton icon="pencil-simple" label="Rename" onClick={() => setRenameOpen(true)} />
                  ) : null}
                  <Link to={`/g/${detail.id}`} className="gk-btn gk-btn--secondary">
                    <Icon name="play" size={20} />
                    Open recording
                  </Link>
                </div>
                <div className="gk-sm notes__meta">
                  <span>{meetingWhen(detail.created_at)}</span>
                  {detail.duration_ms != null ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span className="gk-num">{formatDuration(detail.duration_ms)}</span>
                    </>
                  ) : null}
                  <span aria-hidden="true">·</span>
                  <span>{detail.owner.name}</span>
                </div>
              </div>

              <Tabs
                value={tab}
                onChange={setTab}
                items={[
                  { value: "notes", label: "Notes" },
                  { value: "moments", label: "Moments", count: moments.length > 0 ? String(moments.length) : undefined },
                ]}
                trailing={
                  notes ? (
                    <span className="gk-caption notes__producer">
                      <Icon name="sparkle" size={15} />
                      {notes.producedBy} · only sourced lines shown
                    </span>
                  ) : undefined
                }
              />

              {tab === "moments" ? (
                moments.length === 0 ? (
                  <div className="notes__empty">
                    <Icon name="flag" />
                    <p>No moments were marked in this meeting.</p>
                  </div>
                ) : (
                  <section className="notes__section" aria-label="Moments">
                    {moments.map((m) => (
                      <button
                        type="button"
                        key={m.id}
                        className="notes__moment"
                        onClick={() => setSource(m.at_ms / 1000)}
                      >
                        <Moment text={m.label || "Marked moment"} time={clock(m.at_ms / 1000)} />
                      </button>
                    ))}
                  </section>
                )
              ) : notes ? (
                <>
                  <section className="notes__section">
                    <h3 className="gk-h5">Summary</h3>
                    <p className="gk-notes notes__summary">
                      {notes.summary.map((s, i) => (
                        <span key={i}>
                          {s.text} {ts(s.t)}{" "}
                        </span>
                      ))}
                    </p>
                  </section>
                  <section className="notes__section">
                    <h3 className="gk-h5">Decisions</h3>
                    {notes.decisions.map((d, i) => (
                      <Decision
                        key={i}
                        text={d.text}
                        time={clock(d.t)}
                        active={source === d.t}
                        onTimeClick={() => setSource(d.t)}
                      />
                    ))}
                  </section>
                  <Card title="Action items" size="md">
                    {notes.actions.map((a, i) => (
                      <ActionItem
                        key={i}
                        text={a.text}
                        owner={a.owner ? { name: a.owner, speaker: speakers.get(a.owner) } : undefined}
                        due={a.due}
                        time={clock(a.t)}
                        defaultDone={a.done}
                        onTimeClick={() => setSource(a.t)}
                      />
                    ))}
                  </Card>
                </>
              ) : (
                <div className="notes__empty notes__empty--llm">
                  <span className="notes__empty-icon">
                    <Icon name="sparkle" size={26} />
                  </span>
                  <h2 className="gk-h5">No notes yet</h2>
                  <p>{NOTES_EMPTY}</p>
                  {isAdmin ? (
                    <Link to={adminAiPath(org.slug)} className="gk-btn gk-btn--secondary gk-btn--sm">
                      Open AI settings
                    </Link>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>

        <aside className="notes__source" aria-label="Source">
          <div className="notes__source-head">
            <span className="notes__source-title">Source</span>
            {source !== null ? <Timestamp time={clock(source)} active /> : null}
            {detail ? (
              <Link to={`/g/${detail.id}`} className="notes__source-link">
                Open recording
              </Link>
            ) : null}
          </div>
          <div className="notes__source-video">
            <VideoFrame tone="meeting" radius={14} src={detail?.thumbnail_url ?? undefined} label="meeting">
              {detail ? (
                <Link to={`/g/${detail.id}`} className="gk-video__play" aria-label="Play recording">
                  <Icon name="play" fill size={26} />
                </Link>
              ) : null}
            </VideoFrame>
          </div>
          <div className="notes__source-lines">
            {cues.length === 0 ? (
              <p className="gk-caption notes__source-empty">
                {transcript?.status === "queued" || transcript?.status === "running"
                  ? "The transcript is still being finalized."
                  : "No transcript for this meeting yet."}
              </p>
            ) : (
              cues.map((c, i) => {
                const name = c.speaker ?? "Speaker";
                return (
                  <button type="button" key={`${i}-${c.start}`} className="notes__line" onClick={() => setSource(c.start)}>
                    <Utterance
                      speaker={{ name, speaker: speakers.get(name) }}
                      time={clock(c.start)}
                      text={c.text}
                      highlight={i === sourceIndex}
                    />
                  </button>
                );
              })
            )}
          </div>
          {transcript?.provenance ? (
            <div className="gk-caption notes__source-foot">
              <Badge tone="neutral" size="sm" icon="waveform">
                Transcript
              </Badge>
              {transcript.model_id ? <span>{transcript.model_id}</span> : null}
              <span>{transcript.provenance}</span>
            </div>
          ) : null}
        </aside>
      </div>
      {renameOpen && detail ? (
        <RenameGrackleDialog
          grackleId={detail.id}
          title={detail.title}
          onClose={() => setRenameOpen(false)}
          onRenamed={(title) => setDetail((prev) => (prev ? { ...prev, title } : prev))}
        />
      ) : null}
    </AppShell>
  );
}
